import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeftRight, ArrowRightLeft, ClipboardCheck, ShoppingCart, KeyRound, ShieldAlert, EggOff, History, ClipboardList, Layers } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  getStock, getMovimientos, getPrecios,
  getConteos, createConteo, updateConteo, deleteConteo, getInventarioSistemaActual,
  getInventarioPorLote,
  getCompras, createCompra, updateCompra, deleteCompra,
  verificarSuperCodigo,
} from '../lib/api';
import { formatDate } from '../lib/dateUtils';
import { exportConteoToPDF } from '../lib/exportUtils';
import { useAuth } from '../lib/auth';

import { EGG_LABELS, TIPOS_CONTEO, normalizeTipoKey } from './almacen/constants';
import { StockOverview } from './almacen/StockOverview';
import { MovimientosTab } from './almacen/MovimientosTab';
import { ConteosTab } from './almacen/ConteosTab';
import { ConteoModal } from './almacen/ConteoModal';
import { ComprasTab } from './almacen/ComprasTab';
import { RotosTab } from './almacen/RotosTab';
import { HistorialDiarioTab } from './almacen/HistorialDiarioTab';
import { ConteoDiarioTab } from './almacen/ConteoDiarioTab';
import { TrasladosTab } from './almacen/TrasladosTab';
import { InventarioPorLoteTab } from './almacen/InventarioPorLoteTab';

export default function AlmacenInventario() {
  const { hasModuleAccess, isConteoCiego } = useAuth();
  // Si el usuario está en modo "Conteo Ciego" NUNCA ve las existencias del
  // sistema ni las columnas Sistema/Diferencia, aunque su rol/módulos se lo
  // permitirían normalmente. Super Admin queda exento por auth.js.
  const canVerExistencias = !isConteoCiego && hasModuleAccess('almacen_ver_existencias');
  const canVerSistemaConteo = !isConteoCiego && hasModuleAccess('almacen_ver_sistema_conteo');

  // ----- Stock & movimientos -----
  // Filtros del historial persistidos en localStorage para que no se pierdan
  // al recargar la página.
  const FILTROS_KEY = 'avipork_movimientos_filtros_v1';
  const loadPersistedFilters = () => {
    try {
      const raw = localStorage.getItem(FILTROS_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };
  const persisted = loadPersistedFilters() || {};

  const [stock, setStock] = useState({});
  const [totalStock, setTotalStock] = useState(0);
  const [movimientos, setMovimientos] = useState([]);
  const [precios, setPrecios] = useState({});
  const [loading, setLoading] = useState(true);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState(persisted.filtroTipo || 'all');
  const [filtroMovimiento, setFiltroMovimiento] = useState(persisted.filtroMovimiento || 'all');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState(persisted.filtroFechaDesde || '');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState(persisted.filtroFechaHasta || '');

  // ----- Conteos semanales -----
  const [conteos, setConteos] = useState([]);
  const [loadingConteos, setLoadingConteos] = useState(false);
  const [conteoModalOpen, setConteoModalOpen] = useState(false);
  const [editingConteoId, setEditingConteoId] = useState(null);
  const [conteoForm, setConteoForm] = useState({
    fecha_conteo: new Date().toISOString().split('T')[0],
    conteos: {},
    observaciones: '',
  });
  // Inventario por lote (usado en el conteo semanal para desglose por lote).
  // `lotesPorTipo` es un mapa { tipo: [{lote_id, lote_codigo, disponible, ...}] }
  const [lotesPorTipo, setLotesPorTipo] = useState({});
  const [savingConteo, setSavingConteo] = useState(false);
  const [deletingConteo, setDeletingConteo] = useState(null);
  const [deletingConteoInProgress, setDeletingConteoInProgress] = useState(false);

  // Gate de Super Código para crear Cotejo Inicial
  const [superCodigoModalOpen, setSuperCodigoModalOpen] = useState(false);
  const [superCodigoInput, setSuperCodigoInput] = useState('');
  const [verifyingCodigo, setVerifyingCodigo] = useState(false);
  // Acción pendiente tras verificar el código: 'save' | 'edit' | 'delete'
  // - save:   completa el guardado de un Cotejo Inicial (flujo histórico)
  // - edit:   abre el formulario de edición con el conteo `payload`
  // - delete: borra el conteo `payload`
  const [superCodigoAction, setSuperCodigoAction] = useState({ type: null, payload: null });

  // ----- Compras de huevos -----
  const initialCompraForm = {
    fecha: new Date().toISOString().split('T')[0],
    proveedor: '',
    tipo_huevo: 'grande',
    cantidad: '',
    observaciones: '',
  };
  const [compras, setCompras] = useState([]);
  const [loadingCompras, setLoadingCompras] = useState(false);
  const [compraForm, setCompraForm] = useState(initialCompraForm);
  const [editingCompraId, setEditingCompraId] = useState(null);
  const [savingCompra, setSavingCompra] = useState(false);
  const [deletingCompra, setDeletingCompra] = useState(null);
  const [deletingCompraInProgress, setDeletingCompraInProgress] = useState(false);
  const [comprasError, setComprasError] = useState(null);
  const [filtroCompraTipo, setFiltroCompraTipo] = useState('all');

  // Pestaña activa controlada — evita que se resetee a "movimientos"
  // cuando se re-renderiza el árbol tras un loadData().
  const TAB_KEY = 'avipork_almacen_active_tab_v1';
  const [activeTab, setActiveTab] = useState(() => {
    try { return localStorage.getItem(TAB_KEY) || 'movimientos'; } catch { return 'movimientos'; }
  });
  useEffect(() => {
    try { localStorage.setItem(TAB_KEY, activeTab); } catch { /* noop */ }
  }, [activeTab]);

  // ============================================================
  // Cargas de datos
  // ============================================================
  const loadData = useCallback(async () => {
    try {
      // Solo mostrar spinner full-page en la primera carga; las recargas
      // posteriores son silenciosas para no remontar el árbol de tabs.
      if (!initialLoaded) setLoading(true);
      // Por defecto cargamos los últimos 7 días de movimientos para vista limpia
      const hoy = new Date();
      const hace7 = new Date(hoy.getTime() - 7 * 24 * 60 * 60 * 1000);
      const iso = (d) => d.toISOString().slice(0, 10);
      const [stockRes, movRes, preciosRes] = await Promise.all([
        getStock(),
        getMovimientos({ limit: 5000, fecha_desde: iso(hace7), fecha_hasta: iso(hoy) }),
        getPrecios(),
      ]);
      setStock(stockRes.data.stock);
      setTotalStock(stockRes.data.total);
      setMovimientos(movRes.data);
      setPrecios(preciosRes.data);
    } catch (err) {
      toast.error('Error cargando inventario');
    } finally {
      setLoading(false);
      setInitialLoaded(true);
    }
  }, [initialLoaded]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadMovimientos = useCallback(async () => {
    try {
      // Si el usuario NO especifica rango, usamos los últimos 7 días por defecto.
      // Si especifica rango de fechas, respetamos ese rango.
      const hasDateFilter = !!(filtroFechaDesde || filtroFechaHasta);
      const params = { limit: 5000 };
      if (filtroTipo && filtroTipo !== 'all') params.tipo_huevo = filtroTipo;
      if (filtroMovimiento && filtroMovimiento !== 'all') params.tipo_movimiento = filtroMovimiento;
      if (hasDateFilter) {
        if (filtroFechaDesde) params.fecha_desde = filtroFechaDesde;
        if (filtroFechaHasta) params.fecha_hasta = filtroFechaHasta;
      } else {
        const hoy = new Date();
        const hace7 = new Date(hoy.getTime() - 7 * 24 * 60 * 60 * 1000);
        const iso = (d) => d.toISOString().slice(0, 10);
        params.fecha_desde = iso(hace7);
        params.fecha_hasta = iso(hoy);
      }
      const res = await getMovimientos(params);
      setMovimientos(res.data);
    } catch (err) {
      console.error('Error loading movimientos:', err);
    }
  }, [filtroTipo, filtroMovimiento, filtroFechaDesde, filtroFechaHasta]);

  useEffect(() => { loadMovimientos(); }, [loadMovimientos]);

  // Persistir filtros del historial de movimientos en localStorage
  useEffect(() => {
    try {
      localStorage.setItem(FILTROS_KEY, JSON.stringify({
        filtroTipo,
        filtroMovimiento,
        filtroFechaDesde,
        filtroFechaHasta,
      }));
    } catch {
      /* localStorage puede no estar disponible — silencioso */
    }
  }, [filtroTipo, filtroMovimiento, filtroFechaDesde, filtroFechaHasta]);

  const loadConteos = useCallback(async () => {
    setLoadingConteos(true);
    try {
      const res = await getConteos();
      setConteos(res.data || []);
    } catch (err) {
      toast.error('Error cargando conteos');
    } finally {
      setLoadingConteos(false);
    }
  }, []);

  useEffect(() => { loadConteos(); }, [loadConteos]);

  const loadCompras = useCallback(async () => {
    setLoadingCompras(true);
    setComprasError(null);
    try {
      const params = {};
      if (filtroCompraTipo !== 'all') params.tipo_huevo = filtroCompraTipo;
      const res = await getCompras(params);
      setCompras(res.data || []);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error cargando compras';
      if (err.response?.status === 503) {
        setComprasError(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setLoadingCompras(false);
    }
  }, [filtroCompraTipo]);

  useEffect(() => { loadCompras(); }, [loadCompras]);

  // ============================================================
  // Conteos
  // ============================================================
  const openNewConteo = async () => {
    setEditingConteoId(null);
    try {
      const today = new Date().toISOString().split('T')[0];
      const [sysRes, lotesRes] = await Promise.all([
        getInventarioSistemaActual(today),
        getInventarioPorLote().catch(() => ({ data: { por_tipo: {} } })),
      ]);
      const sis = sysRes.data || {};
      const porTipo = lotesRes.data?.por_tipo || {};
      setLotesPorTipo(porTipo);
      const initial = {};
      TIPOS_CONTEO.forEach((t) => {
        // Preferimos la SUMA por lote (fuente de verdad) sobre el valor global
        // del sistema. Si no hay desglose por lote, cae al valor de /sistema-actual.
        const key = t === 'pequeño' && porTipo.pequeno ? 'pequeno' : t;
        const lotes = porTipo[key] || porTipo[t] || [];
        const sumaLotes = lotes.reduce((s, l) => s + (Number(l.disponible) || 0), 0);
        const valSistema = lotes.length > 0 ? sumaLotes : (sis[t] || 0);
        initial[t] = { sistema: valSistema, fisico: '' };
      });
      // Semilla de conteos por lote: incluye TODOS los lotes con producción
      // histórica del tipo (aunque el FIFO-inverso los haya dejado con
      // disponible=0), para que el usuario pueda registrar el conteo físico
      // en cualquier lote activo, no solo en los que el sistema cree con stock.
      const porLoteInicial = [];
      TIPOS_CONTEO.forEach((t) => {
        const key = t === 'pequeño' && porTipo.pequeno ? 'pequeno' : t;
        const lotes = porTipo[key] || porTipo[t] || [];
        lotes.forEach((L) => {
          const disp = Number(L.disponible || 0);
          const prod = Number(L.producido || 0);
          if (disp > 0 || prod > 0) {
            porLoteInicial.push({
              tipo_huevo: t,
              lote_id: L.lote_id,
              lote_codigo: L.lote_codigo || '',
              sistema: disp,
              fisico: '',
            });
          }
        });
      });
      setConteoForm({
        fecha_conteo: today,
        conteos: initial,
        conteos_por_lote: porLoteInicial,
        observaciones: '',
        cotejo_inicial: false,
      });
      setConteoModalOpen(true);
    } catch (err) {
      toast.error('Error obteniendo inventario actual');
    }
  };

  /**
   * Recarga la columna "Sistema" del conteo con el inventario AS-OF la fecha
   * indicada (ignora movimientos posteriores). Se llama cuando el usuario
   * cambia la fecha en el modal de nuevo/editar conteo.
   *
   * En modo edición no recalculamos: los valores "sistema" del conteo
   * original son el contexto histórico y no deben sobreescribirse.
   */
  const reloadSistemaForFecha = async (fecha) => {
    if (!fecha || editingConteoId) return;
    try {
      const sysRes = await getInventarioSistemaActual(fecha);
      const sis = sysRes.data || {};
      setConteoForm((prev) => {
        const nuevos = { ...prev.conteos };
        TIPOS_CONTEO.forEach((t) => {
          nuevos[t] = { ...(nuevos[t] || {}), sistema: sis[t] || 0 };
        });
        return { ...prev, conteos: nuevos };
      });
    } catch (err) {
      console.warn('Error recargando sistema as-of fecha:', err);
    }
  };

  // Helper: vuelca un conteo al formulario y abre el modal de edición
  const _hydrateConteoForm = async (c) => {
    setEditingConteoId(c.id);
    const conteosObj = {};
    TIPOS_CONTEO.forEach((t) => {
      const item = c.conteos?.[t] || c.conteos?.[t === 'pequeño' ? 'pequeno' : t] || {};
      conteosObj[t] = {
        sistema: Number(item.sistema || 0),
        fisico: item.fisico != null ? String(item.fisico) : '',
      };
    });
    // Restaurar desglose por lote guardado en `_por_lote` (nuevo). Si no existe
    // (conteo antiguo), intentar reconstruirlo con el inventario por lote actual
    // — solo como guía visual; los valores físicos quedan vacíos.
    let porLote = Array.isArray(c.conteos?._por_lote) ? [...c.conteos._por_lote] : [];
    let porTipo = lotesPorTipo;
    if (porLote.length === 0) {
      try {
        const lotesRes = await getInventarioPorLote();
        porTipo = lotesRes.data?.por_tipo || {};
        setLotesPorTipo(porTipo);
        TIPOS_CONTEO.forEach((t) => {
          const key = t === 'pequeño' && porTipo.pequeno ? 'pequeno' : t;
          const lotes = porTipo[key] || porTipo[t] || [];
          lotes.forEach((L) => {
            const disp = Number(L.disponible || 0);
            const prod = Number(L.producido || 0);
            if (disp > 0 || prod > 0) {
              porLote.push({
                tipo_huevo: t,
                lote_id: L.lote_id,
                lote_codigo: L.lote_codigo || '',
                sistema: disp,
                fisico: '',
              });
            }
          });
        });
      } catch (_) { /* silencioso */ }
    } else {
      // Asegurar que las cadenas de fisico se manejen como string en el form
      porLote = porLote.map((r) => ({
        ...r,
        fisico: r.fisico != null ? String(r.fisico) : '',
      }));
    }
    setConteoForm({
      fecha_conteo: c.fecha_conteo?.split('T')[0] || new Date().toISOString().split('T')[0],
      conteos: conteosObj,
      conteos_por_lote: porLote,
      observaciones: c.observaciones || '',
      cotejo_inicial: !!(c.conteos?._meta?.cotejo_inicial),
    });
    setConteoModalOpen(true);
  };

  // Editar conteo requiere código de Super Usuario
  const openEditConteo = (c) => {
    setSuperCodigoAction({ type: 'edit', payload: c });
    setSuperCodigoInput('');
    setSuperCodigoModalOpen(true);
  };

  // Eliminar conteo requiere código de Super Usuario
  const requestDeleteConteo = (c) => {
    setSuperCodigoAction({ type: 'delete', payload: c });
    setSuperCodigoInput('');
    setSuperCodigoModalOpen(true);
  };

  // Ejecuta el guardado real (separado para reutilizar tras verificar código)
  const executeSaveConteo = async () => {
    setSavingConteo(true);
    try {
      const payload = {
        fecha_conteo: conteoForm.fecha_conteo,
        observaciones: conteoForm.observaciones,
        cotejo_inicial: !!conteoForm.cotejo_inicial,
        conteos: TIPOS_CONTEO.reduce((acc, t) => {
          const c = conteoForm.conteos[t] || {};
          acc[t] = {
            sistema: Number(c.sistema || 0),
            fisico: parseInt(c.fisico, 10) || 0,
          };
          return acc;
        }, {}),
        // Persistir el desglose por lote como metadata (nuevo requisito).
        // Se envía solo cuando NO es cotejo_inicial (el cotejo inicial suma al total
        // global; el desglose por lote se conservará vacío en ese caso).
        conteos_por_lote: (conteoForm.conteos_por_lote || [])
          .filter((r) => r.lote_id)
          .map((r) => ({
            tipo_huevo: r.tipo_huevo,
            lote_id: r.lote_id,
            lote_codigo: r.lote_codigo || '',
            sistema: Number(r.sistema || 0),
            fisico: parseInt(r.fisico, 10) || 0,
          })),
      };
      let savedConteo = null;
      if (editingConteoId) {
        const res = await updateConteo(editingConteoId, payload);
        savedConteo = res?.data || null;
        toast.success(
          conteoForm.cotejo_inicial
            ? 'Cotejo inicial actualizado · inventario sumado'
            : 'Conteo actualizado · inventario ajustado al físico'
        );
      } else {
        const res = await createConteo(payload);
        savedConteo = res?.data || null;
        toast.success(
          conteoForm.cotejo_inicial
            ? 'Cotejo inicial registrado · inventario sumado correctamente'
            : 'Conteo registrado · inventario ajustado al físico'
        );
      }
      setConteoModalOpen(false);
      setEditingConteoId(null);
      // Recargar inventario + movimientos porque el conteo los afecta
      await Promise.all([loadConteos(), loadData(), loadMovimientos()]);

      // Generar PDF automáticamente con los datos del conteo recién guardado
      if (savedConteo) {
        try {
          const pdfResult = exportConteoToPDF(savedConteo, 'save');
          if (pdfResult === 'saved-fallback') {
            toast.warning('Pop-up bloqueado · se descargó el PDF igual');
          } else if (pdfResult) {
            toast.success('PDF del conteo descargado', { duration: 3000 });
          }
        } catch (pdfErr) {
          console.warn('Error generando PDF del conteo:', pdfErr);
          toast.warning('Conteo guardado pero no se pudo generar el PDF');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando conteo');
    } finally {
      setSavingConteo(false);
    }
  };

  const handleSaveConteo = async () => {
    if (!conteoForm.fecha_conteo) {
      toast.error('Seleccione una fecha de conteo');
      return;
    }
    // Si es Cotejo Inicial (nuevo), pedir código de Super Usuario primero
    if (conteoForm.cotejo_inicial && !editingConteoId) {
      setSuperCodigoAction({ type: 'save', payload: null });
      setSuperCodigoInput('');
      setSuperCodigoModalOpen(true);
      return;
    }
    await executeSaveConteo();
  };

  // Verifica el código de super-usuario y ejecuta la acción pendiente.
  // Soporta: save (cotejo inicial), edit (abrir form edición), delete (borrar conteo).
  const handleVerificarSuperCodigoConteo = async () => {
    const codigo = (superCodigoInput || '').trim();
    if (codigo.length < 4) {
      toast.error('Ingrese el código de Super Usuario');
      return;
    }
    setVerifyingCodigo(true);
    try {
      await verificarSuperCodigo(codigo);
      const { type, payload } = superCodigoAction;
      setSuperCodigoModalOpen(false);
      setSuperCodigoInput('');
      setSuperCodigoAction({ type: null, payload: null });

      // Ejecutar la acción correspondiente
      if (type === 'save') {
        await executeSaveConteo();
      } else if (type === 'edit' && payload) {
        _hydrateConteoForm(payload);
      } else if (type === 'delete' && payload) {
        setDeletingConteo(payload);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Código de Super Usuario incorrecto');
    } finally {
      setVerifyingCodigo(false);
    }
  };

  const handleConfirmDeleteConteo = async () => {
    if (!deletingConteo) return;
    setDeletingConteoInProgress(true);
    try {
      await deleteConteo(deletingConteo.id);
      toast.success('Conteo eliminado');
      setDeletingConteo(null);
      loadConteos();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error eliminando conteo');
    } finally {
      setDeletingConteoInProgress(false);
    }
  };

  const exportarConteoPDF = (conteo, mode = 'save') => {
    const ok = exportConteoToPDF(conteo, mode);
    if (ok === 'saved-fallback') {
      toast.warning('Pop-up bloqueado. Se descargó el PDF.');
    } else if (ok) {
      toast.success(mode === 'print' ? 'Abriendo impresión...' : 'PDF descargado');
    } else {
      toast.error('Error generando PDF');
    }
  };

  // ============================================================
  // Compras
  // ============================================================
  const setCompraField = (field, value) => {
    setCompraForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetCompraForm = () => {
    setCompraForm(initialCompraForm);
    setEditingCompraId(null);
  };

  const handleEditCompra = (c) => {
    setEditingCompraId(c.id || c._id);
    setCompraForm({
      fecha: c.fecha?.split('T')[0] || new Date().toISOString().split('T')[0],
      proveedor: c.proveedor || '',
      tipo_huevo: normalizeTipoKey(c.tipo_huevo) || 'grande',
      cantidad: String(c.cantidad ?? ''),
      observaciones: c.observaciones || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveCompra = async (forzarDuplicado = false) => {
    if (!compraForm.fecha) { toast.error('Seleccione una fecha'); return; }
    if (!compraForm.proveedor.trim()) { toast.error('Ingrese el nombre del proveedor'); return; }
    const cant = parseInt(compraForm.cantidad, 10);
    if (!cant || cant <= 0) { toast.error('La cantidad debe ser mayor a 0'); return; }

    setSavingCompra(true);
    try {
      const payload = {
        fecha: compraForm.fecha,
        proveedor: compraForm.proveedor.trim(),
        tipo_huevo: compraForm.tipo_huevo,
        cantidad: cant,
        observaciones: compraForm.observaciones?.trim() || null,
      };
      if (editingCompraId) {
        await updateCompra(editingCompraId, payload);
        toast.success('Compra actualizada e inventario ajustado');
      } else {
        await createCompra(payload, forzarDuplicado ? { confirmar_duplicado: true } : {});
        toast.success(
          `Compra registrada · +${cant.toLocaleString('en-US')} ${EGG_LABELS[compraForm.tipo_huevo] || compraForm.tipo_huevo} al inventario`,
        );
      }
      resetCompraForm();
      await Promise.all([loadCompras(), loadData(), loadMovimientos()]);
    } catch (err) {
      // Caso especial: backend detectó posible duplicado (HTTP 409)
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409 && detail && typeof detail === 'object' && detail.error === 'compra_duplicada') {
        // Pedimos confirmación al usuario antes de crear el "duplicado"
        const confirmar = window.confirm(
          `${detail.message}\n\n¿Crear esta compra de todos modos?`,
        );
        if (confirmar) {
          // Reintentamos pasando la confirmación
          setSavingCompra(false);
          return handleSaveCompra(true);
        }
      } else {
        const msg = typeof detail === 'string' ? detail : (detail?.message || 'Error guardando compra');
        toast.error(msg);
      }
    } finally {
      setSavingCompra(false);
    }
  };

  const handleConfirmDeleteCompra = async () => {
    if (!deletingCompra) return;
    setDeletingCompraInProgress(true);
    try {
      await deleteCompra(deletingCompra.id || deletingCompra._id);
      toast.success('Compra eliminada · inventario revertido');
      setDeletingCompra(null);
      await Promise.all([loadCompras(), loadData(), loadMovimientos()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error eliminando compra');
    } finally {
      setDeletingCompraInProgress(false);
    }
  };

  // ============================================================
  // Render
  // ============================================================
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const compraDelLabel =
    deletingCompra && (EGG_LABELS[normalizeTipoKey(deletingCompra.tipo_huevo)] || '');

  return (
    <div className="space-y-6">
      {canVerExistencias && (
        <StockOverview stock={stock} totalStock={totalStock} precios={precios} />
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="movimientos" className="gap-2">
            <ArrowLeftRight className="w-4 h-4" /> Movimientos
          </TabsTrigger>
          <TabsTrigger value="conteos" className="gap-2">
            <ClipboardCheck className="w-4 h-4" /> Conteo Semanal
          </TabsTrigger>
          <TabsTrigger value="compras" className="gap-2" data-testid="tab-compras">
            <ShoppingCart className="w-4 h-4" /> Compra de Huevos
          </TabsTrigger>
          <TabsTrigger value="rotos" className="gap-2" data-testid="tab-rotos">
            <EggOff className="w-4 h-4" /> Rotos
          </TabsTrigger>
          <TabsTrigger value="conteo-diario" className="gap-2" data-testid="tab-conteo-diario">
            <ClipboardList className="w-4 h-4" /> Conteo Diario
          </TabsTrigger>
          {!isConteoCiego && (
            <TabsTrigger value="historial" className="gap-2" data-testid="tab-historial-diario">
              <History className="w-4 h-4" /> Historial Diario
            </TabsTrigger>
          )}
          <TabsTrigger value="traslados" className="gap-2" data-testid="tab-traslados">
            <ArrowRightLeft className="w-4 h-4" /> Traslados
          </TabsTrigger>
          <TabsTrigger value="por-lote" className="gap-2" data-testid="tab-por-lote">
            <Layers className="w-4 h-4" /> Por Lote
          </TabsTrigger>
        </TabsList>

        <TabsContent value="movimientos">
          <MovimientosTab
            movimientos={movimientos}
            filtroMovimiento={filtroMovimiento}
            setFiltroMovimiento={setFiltroMovimiento}
            filtroTipo={filtroTipo}
            setFiltroTipo={setFiltroTipo}
            filtroFechaDesde={filtroFechaDesde}
            setFiltroFechaDesde={setFiltroFechaDesde}
            filtroFechaHasta={filtroFechaHasta}
            setFiltroFechaHasta={setFiltroFechaHasta}
          />
        </TabsContent>

        <TabsContent value="conteos">
          <ConteosTab
            conteos={conteos}
            loadingConteos={loadingConteos}
            canVerSistemaConteo={canVerSistemaConteo}
            onNuevoConteo={openNewConteo}
            onEditConteo={openEditConteo}
            onDeleteConteo={requestDeleteConteo}
            onExportPDF={exportarConteoPDF}
          />
        </TabsContent>

        <TabsContent value="compras">
          <ComprasTab
            compras={compras}
            loadingCompras={loadingCompras}
            comprasError={comprasError}
            compraForm={compraForm}
            setCompraField={setCompraField}
            editingCompraId={editingCompraId}
            savingCompra={savingCompra}
            onSaveCompra={handleSaveCompra}
            onResetCompra={resetCompraForm}
            onEditCompra={handleEditCompra}
            onDeleteCompra={setDeletingCompra}
            filtroCompraTipo={filtroCompraTipo}
            setFiltroCompraTipo={setFiltroCompraTipo}
          />
        </TabsContent>

        <TabsContent value="rotos">
          <RotosTab
            onAfterChange={() => { loadData(); loadMovimientos(); }}
          />
        </TabsContent>

        <TabsContent value="conteo-diario">
          <ConteoDiarioTab />
        </TabsContent>

        {!isConteoCiego && (
          <TabsContent value="historial">
            <HistorialDiarioTab />
          </TabsContent>
        )}

        <TabsContent value="traslados">
          <TrasladosTab />
        </TabsContent>

        <TabsContent value="por-lote">
          <InventarioPorLoteTab />
        </TabsContent>
      </Tabs>

      <ConteoModal
        open={conteoModalOpen}
        onOpenChange={(o) => { setConteoModalOpen(o); if (!o) setEditingConteoId(null); }}
        editing={!!editingConteoId}
        canVerSistemaConteo={canVerSistemaConteo}
        form={conteoForm}
        setForm={setConteoForm}
        savingConteo={savingConteo}
        onSave={handleSaveConteo}
        onFechaChange={reloadSistemaForFecha}
        lotesPorTipo={lotesPorTipo}
        stockGlobal={stock}
      />

      {/* Confirmar eliminar conteo */}
      <AlertDialog open={!!deletingConteo} onOpenChange={(o) => { if (!o) setDeletingConteo(null); }}>
        <AlertDialogContent data-testid="confirmar-eliminar-conteo">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este conteo?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el conteo del{' '}
              <strong>{deletingConteo ? formatDate(deletingConteo.fecha_conteo) : ''}</strong>.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingConteoInProgress}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteConteo}
              disabled={deletingConteoInProgress}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="btn-confirmar-eliminar-conteo"
            >
              {deletingConteoInProgress ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmar eliminar compra */}
      <AlertDialog open={!!deletingCompra} onOpenChange={(o) => { if (!o) setDeletingCompra(null); }}>
        <AlertDialogContent data-testid="confirmar-eliminar-compra">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta compra?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará la compra del{' '}
              <strong>{deletingCompra ? formatDate(deletingCompra.fecha) : ''}</strong> a{' '}
              <strong>{deletingCompra?.proveedor}</strong> (
              {Number(deletingCompra?.cantidad || 0).toLocaleString('en-US')} {compraDelLabel}).
              <br /><br />
              <span className="text-amber-700 dark:text-amber-400">
                ⚠ Esta acción restará la cantidad del inventario general. No se puede deshacer.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingCompraInProgress}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteCompra}
              disabled={deletingCompraInProgress}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="btn-confirmar-eliminar-compra"
            >
              {deletingCompraInProgress ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Modal: Código de Super Usuario (gate para Cotejo Inicial, Editar y Eliminar conteos) */}
      <Dialog
        open={superCodigoModalOpen}
        onOpenChange={(o) => {
          if (!o) {
            setSuperCodigoModalOpen(false);
            setSuperCodigoInput('');
            setSuperCodigoAction({ type: null, payload: null });
          }
        }}
      >
        <DialogContent className="sm:max-w-md" data-testid="modal-super-codigo-cotejo">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
              <KeyRound className="w-5 h-5" />
              Código de Super Usuario requerido
            </DialogTitle>
            <DialogDescription className="pt-1">
              {superCodigoAction.type === 'edit' && (
                <>Está por <strong>editar</strong> un conteo semanal existente. Por seguridad, ingrese el código de Super Usuario para continuar.</>
              )}
              {superCodigoAction.type === 'delete' && (
                <>Está por <strong>eliminar</strong> un conteo semanal existente. Esta acción es irreversible. Ingrese el código de Super Usuario para continuar.</>
              )}
              {(superCodigoAction.type === 'save' || !superCodigoAction.type) && (
                <>El <strong>Cotejo de Inventario Inicial</strong> suma huevos directamente al stock.
                Por seguridad, ingrese el código de Super Usuario para continuar.</>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="flex items-start gap-2 text-xs bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-md p-2.5">
              <ShieldAlert className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-amber-900 dark:text-amber-200">
                {superCodigoAction.type === 'delete'
                  ? 'Al eliminar este conteo, los ajustes que generó se revertirán del inventario. Solicite el código al Super Usuario.'
                  : 'Esta acción afecta el inventario general. Solicite el código al Super Usuario.'}
              </p>
            </div>
            <div>
              <Label htmlFor="super-codigo-cotejo-input">Código</Label>
              <Input
                id="super-codigo-cotejo-input"
                type="password"
                value={superCodigoInput}
                onChange={(e) => setSuperCodigoInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !verifyingCodigo) handleVerificarSuperCodigoConteo();
                }}
                placeholder="Ingrese el código"
                className="h-11 mt-1.5 font-mono"
                autoFocus
                data-testid="input-super-codigo-cotejo"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSuperCodigoModalOpen(false);
                setSuperCodigoInput('');
                setSuperCodigoAction({ type: null, payload: null });
              }}
              disabled={verifyingCodigo}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleVerificarSuperCodigoConteo}
              disabled={verifyingCodigo || superCodigoInput.trim().length < 4}
              className={superCodigoAction.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : ''}
              data-testid="btn-verificar-super-codigo-cotejo"
            >
              {verifyingCodigo
                ? 'Verificando...'
                : superCodigoAction.type === 'edit'
                  ? 'Confirmar y editar'
                  : superCodigoAction.type === 'delete'
                    ? 'Confirmar y eliminar'
                    : 'Confirmar y sumar al inventario'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
